import type { PaletteOptions } from '@mui/material/styles';
import primitivesApp from 'themes/primitives-app';
import primitives from 'themes/primitives';

const { base, gray, blue, green, yellow, red, orange, pink, purple, teal, overlay, jadeite, blueberry, gold, blueAlpha, alpha } = primitivesApp;
const { white } = base;
const { brand, gray: pGray, blue: pBlue, green: pGreen, red: pRed, yellow: pYellow, purple: pPurple } = primitives;

const paletteLight: PaletteOptions = {
  // ---- MUI Standard Palette ----
  primary: {
    light: blue[900],
    main: blue[930],
    dark: blue[990],
    contrastText: white,
  },
  secondary: {
    light: blue[650],
    main: blue[800],
    dark: blue[1025],
    contrastText: white,
  },
  success: {
    light: green[200],
    main: green[850],
    dark: green[900],
    contrastText: white,
  },
  info: {
    light: blue[100],
    main: blue[700],
    dark: blue[925],
    contrastText: white,
    surface: blue[50],
  },
  warning: {
    light: yellow[200],
    main: yellow[650],
    dark: yellow[700],
    contrastText: white,
  },
  error: {
    light: red[250],
    main: red[500],
    dark: red[800],
    contrastText: white,
  },

  // ---- Surface ----
  surface: {
    page: gray[150],
    default: gray[0],
    subtle: gray[375],
    raised: gray[0],
    overlay: gray[0],
    sunken: gray[150],
    disabled: gray[450],
    inverse: gray[1250],
    brand: blue[930],
    brandSubtle: blue[50],
    success: green[50],
    warning: yellow[100],
    danger: red[50],
    info: blue[50],
    selected: blue[80],
    hover: blue[50],
    positive: green[50],
    positiveStrong: green[500],
    negative: red[50],
    negativeStrong: red[500],
    nav: blue[930],
    navHover: overlay.black[20],
    navSelected: overlay.black[30],
    accountsBar: gray[450],
    tableGroupHeader: gray[375],
    regTableRow: blueAlpha[20],
    chipsSettings: gray[450],
    subtle2: gray[150],
    subtle3: blue[150],
    transaction: gray[50],
    expense: yellow[100],
    income: green[50],
    overdue: red[50],
    tooltip: gray[1250],
    calendar: gray[500], 
  },

  // ---- Text ----
  text: {
    primary: gray[1200],
    secondary: gray[1025],
    tertiary: gray[975],
    disabled: gray[725],
    contrastText: white,
    strong: gray[1250],
    input: gray[975],
    warning: yellow[700],
    error: red[600],
    // new from XD
    inverse: base.white,
    brand: blue[930],
    brandSubtle: blue[950],
    onBrand: base.white,
    success: green[750],
    danger: red[600],
    info: blue[800],
    positive: green[750],
    negative: red[600],
    placeholder: gray[850],
    onPositive: base.white,
    onNegative: base.white,
    onNav: blue[100],
  },

  // ---- Icons ----
  icon: {
    light: gray[725],
    main: gray[1025],
    dark: gray[1200],
    brand: blue[200],
    muted: gray[950],
    secondary: gray[1025],
    primary: gray[1250],
    // new from XD
    tertiary: gray[725],
    disabled: gray[550],
    inverse: base.white,
    onBrand: base.white,
    success: pGreen[600],
    warning: pYellow[600],
    danger: pRed[600],
    info: pBlue[600],
    positive: pGreen[600],
    negative: pRed[600],
    nav: blue[200],
  },

  // ---- MUI Action States ----
  // NOTE: XD defines action.{primary,secondary,destructive,ghost,link,custom} as button-variant
  // tokens — a structural conflict with these MUI state primitives. XD button variants are
  // available under `button` below.
  action: {
    active: gray[1025],
    hover: gray[300],
    hoverLight: overlay.white[50],
    hoverOpacity: 0.08,
    selected: blue[150],
    selectedDark: overlay.black[10],
    selectedOpacity: 0.08,
    disabled: gray[550],
    disabledBackground: gray[300],
    disabledOpacity: 0.45,
    focus: blue[150],
    focusOpacity: 0.12,
    activatedOpacity: 0.12,
    overlay: overlay.black[7],
    surface: blue[930],
    selectedDefault: overlay.white[12],
  },

  // ---- Backgrounds ----
  background: {
    default: gray[150],
    paper: white,
    layer1: gray[200],
    subtle: gray[375],
    elevated: gray[450],
    input: gray[0],
    hint: yellow[150],        // Usage profile subscription warning
    hintSubtle: yellow[50],   // Dashboard sync notice highlight (barely-visible pale yellow)
    info: blue[221], // Used only in share screen
    selected: blueberry[50], // Used only txn filter
    payeeHighlight: green[75], // Used only in complex rule,
    settingsBarBackground: blue[150], // Report settings filter
    dark: blue[930],
    authDialog: white,
    sunken: gray[150],
    positive: green[50],
    positiveStrong: green[500],
  },

  // ---- Backdrop / Overlays ----
  backdrop: {
    fieldHover: overlay.black[20],
    iconDimmed: overlay.black[50],
    registerSplits: blueAlpha[20],
    navHover: overlay.black[20],     // fixed overlay — nav bg is always blue
    navSelected: overlay.black[30],
  },

  // ---- Borders ----
  border: {
    default: gray[550],
    light: gray[300],
    main: gray[400],
    strong: gray[500],
    dark: gray[550],
    focus: blueberry[100],
    calendarFocus: blueberry[70], // Only one use
    // new from XD
    subtle: gray[500],
    inverse: pGray[50],
    disabled: pGray[200],
    brand: blue[930],
    brandSubtle: blue[900],
    success: green[850],
    warning: yellow[500],
    danger: red[500],
    info: blue[850],
    selected: blue[950],
    shadow: gray[975],
  },

  divider: overlay.black[12],

  // ---- Tooltip ----
  tooltip: {
    light: gray[250],
    dark: gray[1250],
    text: gray[1050],
    contrastText: white,  // text on dark tooltip background
  },

  // ---- State ----
  state: {
    hover: {
      default: alpha.black[7],
      brand: brand[50],
      danger: pRed[50],
      accountsBar: overlay.white[50],
    },
    pressed: {
      default: pGray[200],
      brand: brand[100],
      danger: pRed[100],
    },
    focus: {
      ring: brand[500],
      ringOffset: base.white,
    },
    selected: {
      default: alpha.black[7],
      strong: brand[100],
      accountsBar: overlay.black[10],
    },
    disabled: {
      surface: gray[250],
      border: pGray[200],
      text: pGray[400],
    },
  },

  // ---- Feedback ----
  feedback: {
    success: {
      surface: pGreen[50],
      surfaceStrong: pGreen[100],
      border: pGreen[300],
      text: pGreen[700],
      icon: pGreen[600],
    },
    warning: {
      surface: yellow[50],
      surfaceStrong: yellow[350],
      border: yellow[400],
      text: yellow[700],
      icon: yellow[700],
    },
    danger: {
      surface: pRed[50],
      surfaceStrong: pRed[100],
      border: pRed[300],
      text: pRed[700],
      icon: pRed[600],
    },
    info: {
      surface: pBlue[50],
      surfaceStrong: pBlue[100],
      border: pBlue[300],
      text: pBlue[700],
      icon: pBlue[600],
    },
    neutral: {
      surface: pGray[50],
      surfaceStrong: pGray[100],
      border: pGray[300],
      text: pGray[700],
      icon: pGray[600],
    },
  },

  // ---- Button Variants (from XD action — kept separate to avoid MUI action conflict) ----
  button: {
    custom: {
      settingsTabSelected: gray[450],
    },
    primary: {
      surface: blue[930],
      surfaceHover: blue[950],
      surfacePressed: blue[990],
      surfaceDisabled: gray[450],
      text: base.white,
      textDisabled: gray[550],
      icon: base.white,
    },
    secondary: {
      surface: base.transparent,
      surfaceHover: blue[50],
      surfacePressed: blue[200],
      surfaceDisabled: base.transparent,
      border: blue[930],
      borderHover: blue[950],
      borderDisabled: gray[450],
      text: blue[930],
      textDisabled: gray[550],
      icon: blue[930],
    },
    destructive: {
      surface: pRed[600],
      surfaceHover: pRed[700],
      surfacePressed: pRed[800],
      surfaceDisabled: pGray[200],
      text: base.white,
      textDisabled: pGray[400],
      icon: base.white,
    },
    ghost: {
      surface: base.transparent,
      surfaceHover: pGray[100],
      surfacePressed: pGray[200],
      surfaceDisabled: base.transparent,
      text: brand[500],
      textDisabled: pGray[400],
      icon: brand[500],
    },
    link: {
      text: blue[930],
      textHover: brand[600],
      textVisited: pPurple[600],
      textDisabled: pGray[400],
    },
  },

  // ---- Overlay Scale ----
  overlayScale: {
    black10: alpha.black[10],
    black20: alpha.black[20],
    black30: alpha.black[30],
    black40: alpha.black[40],
    black50: alpha.black[50],
    black60: alpha.black[60],
    white10: alpha.white[10],
    white20: alpha.white[20],
    white30: alpha.white[30],
    white40: alpha.white[40],
    white50: alpha.white[50],
    white60: alpha.white[60],
  },

  // ---- Accent ----
  accent: {
    surface: brand[50],
    surfaceStrong: brand[100],
    border: brand[300],
    text: brand[700],
    icon: brand[600],
    highlight: brand[500],
  },

  // ---- Amount / Numbers ----
  amount: {
    positive: green[700],
    negative: red[750],
  },

  // ---- Charts ----
  charts: {
    positive: green[850],
    negative: red[400],
  },

  // ---- Data Sequence (named, from XD) ----
  dataSequence: {
    blue: blue[700],
    yellow: yellow[750],
    green: green[500],
    orange: orange[500],
    purple: purple[300],
    pink: pink[200],
    teal: teal[100],
    purple2: purple[100],
    teal2: teal[400],
    orange2: orange[600],
  },

  // ---- Avatar ----
  avatar: {
    green: green[700],
    blue: blue[925],
    gray: gray[850],
    grayLarge: gray[550],
  },

  // ---- Badges ----
  badges: {
    orange: yellow[300],
  },

  // ---- Severity Status (budget, health scores, etc.) ----
  status: {
    lowLight: green[400],
    mediumMain: yellow[650],
    highMain: red[500],
    attentionLight: gold[30],
    attentionMain: gold[100],
    dangerMain: red[600],
    incomeLight: green[50], // Calendar
    expenseLight: yellow[175], // Calendar
    overdueLight: red[50], // Calendar
    surfaceStrong: yellow[350],
  },

  // ---- Data Visualization (indexed array, legacy) ----
  data: {
    sequence1: [
      blue[700], yellow[750], green[500], orange[500],
      purple[300], pink[200], teal[100], purple[100],
      teal[500], orange[600],
    ],
  },

  // ---- Legend ----
  legend: {
    income: green[700],
    bill: blue[1030],
  },

  // ---- Links ----
  link: {
    main: blue[750],
    dark: blue[930],
    interactive: blue[1030],
  },

  // ---- App-Specific ----
  other: {
    overdueTxn: yellow[500],
    dueTxn: blue[1075],
    scheduledChip: yellow[300],
  },

  reports: {
    incomeColor: jadeite[100], // Budget & calendar card
    positiveColor: green[850], // charts
    negativeColor: red[400], // charts
    positiveDimmed: green[100], // charts
    negativeDimmed: red[100], // charts
  },

  checkbox: {
    background: gray[150],
    border: gray[550],
    color: gray[1250],
  },

  cuecards: {
    surface: blue[1025],
  },

  icons: {
    svgFilter: 'none',
  },

};

export default paletteLight;
